import { FC, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { isSameDay } from 'date-fns';
import { ArrowRight, CalendarClock } from 'lucide-react';
import {
  ExitSlipCascadeMove,
  ExitSlipMoveStrategy,
  ExitSlipPastTag,
  ExitSlipRescheduleRow,
  lessonName,
  planExitSlipCascade,
  shortDay,
} from '../../../businessLogicService/exitSlipSnapshotHelper';
import ExitSlipCalendar from '../../ExitSlip/ExitSlipCalendar';
import { useViewportScrollLock } from '../../../custom-hooks/useViewportScrollLock';

interface Props {
  schedule: ExitSlipRescheduleRow[];
  pastTags: ExitSlipPastTag[];
  blocked: Date[];
  canceled: Date[];
  movingIndex: number;
  className: string;
  responseCount: number;
  maxDate: Date | null;
  saving: boolean;
  onClose: () => void;
  onConfirm: (target: Date, moves: ExitSlipCascadeMove[], replaced?: ExitSlipRescheduleRow) => void;
}

const strategyPillClass = (selected: boolean, disabled = false) =>
  `tw-flex tw-h-8 tw-flex-1 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-text-[13px] tw-font-bold tw-leading-[18px] ${
    disabled
      ? 'tw-cursor-not-allowed tw-bg-transparent tw-text-[#9C9B9B]'
      : selected
      ? 'tw-cursor-pointer tw-bg-indigo-700 tw-text-white'
      : 'tw-cursor-pointer tw-bg-transparent tw-text-neutral-950 hover:tw-bg-black/5'
  }`;

const moveRow = (title: string, from: Date, to: Date, muted = false) => (
  <div
    key={`${title}-${to.getTime()}`}
    className={`tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-3 tw-py-1 ${
      muted ? 'tw-bg-white/75' : 'tw-bg-white'
    }`}
  >
    <span
      className={`tw-min-w-0 tw-grow tw-truncate tw-text-[13px] tw-leading-[18px] tw-text-neutral-950 ${
        muted ? '' : 'tw-font-bold'
      }`}
    >
      {lessonName(title)}
    </span>
    <span className="tw-shrink-0 tw-text-xs tw-leading-4 tw-text-neutral-600 tw-line-through">{shortDay(from)}</span>
    <ArrowRight size={12} strokeWidth={3} className="tw-shrink-0 tw-text-[#2E9079]" />
    <span className="tw-shrink-0 tw-text-xs tw-font-bold tw-leading-4 tw-text-[#23725F]">{shortDay(to)}</span>
  </div>
);

const ExitSlipSnapshotRescheduleModal: FC<Props> = ({
  schedule,
  pastTags,
  blocked,
  canceled,
  movingIndex,
  className,
  responseCount,
  maxDate,
  saving,
  onClose,
  onConfirm,
}) => {
  const [target, setTarget] = useState<Date | null>(null);
  const [strategy, setStrategy] = useState<ExitSlipMoveStrategy>('push');
  useViewportScrollLock();

  const mover = schedule[movingIndex];
  const moverTitle = mover.title;
  const source = mover.date;

  const dates = schedule.map(row => row.date);
  //the cascade planner blocks by day alone, so these cells stay untagged
  const blockedTags = blocked.map(date => ({ date, title: '' }));
  //TEAMB-3497 replacing unassigns the sitting ticket rather than rehoming it, so nothing moves
  //but the mover. The calendar previews hovered days too, hence the raw pick over the
  //target-derived `effective` below — otherwise hovering an occupied day would preview a plan
  //the click wouldn't carry out.
  const plan = (day: Date | null) =>
    strategy === 'replace' ? [] : planExitSlipCascade(day, movingIndex, dates, blocked);

  const moves = plan(target);
  const colliding = target
    ? schedule.find((row, index) => index !== movingIndex && isSameDay(row.date, target))
    : undefined;
  const collidingTitle = colliding?.title ?? '';

  //TEAMB-3498 an open day has nothing to push, so the lesson moves alone whatever the pills hold
  const effective: ExitSlipMoveStrategy = colliding ? strategy : 'replace';
  const replaced = effective === 'replace' ? colliding : undefined;

  return (
    <Dialog
      visible
      modal
      closable={false}
      showHeader={false}
      onHide={onClose}
      pt={{
        root: {
          className:
            '!tw-w-[800px] !tw-max-w-[800px] !tw-rounded-2xl !tw-border-4 !tw-border-solid !tw-border-[#F1F0EE]',
        },
        content: { className: '!tw-rounded-2xl !tw-p-8 tw-font-atkinson' },
      }}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-min-w-0 tw-grow tw-font-sora tw-text-xl tw-font-bold tw-leading-7 tw-text-neutral-950">
            Reschedule {lessonName(moverTitle)}
          </span>
          <span className="tw-shrink-0 tw-rounded-full tw-border-2 tw-border-solid tw-border-stone-100 tw-bg-stone-50 tw-px-4 tw-py-1.5 tw-text-sm tw-font-bold tw-leading-5 tw-text-neutral-950">
            {className}
          </span>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-items-stretch tw-gap-6">
            <ExitSlipCalendar
              schedule={schedule}
              pastTags={pastTags}
              blocked={blockedTags}
              canceled={canceled}
              movingIndex={movingIndex}
              target={target}
              maxDate={maxDate}
              plan={plan}
              onPick={setTarget}
            />

            <div className="tw-flex tw-min-w-0 tw-grow tw-flex-col tw-gap-2.5">
              {!target ? (
                <div className="tw-flex tw-grow tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-3xl tw-bg-[#F9F8F5] tw-px-6">
                  <CalendarClock size={20} className="tw-text-neutral-950" />
                  <div className="tw-flex tw-flex-col tw-gap-2 tw-px-8 tw-text-center">
                    <span className="tw-text-base tw-font-bold tw-leading-5 tw-text-neutral-950">
                      Pick a date to reschedule today&apos;s lesson
                    </span>
                    <span className="tw-text-sm tw-leading-5 tw-text-[#686868]">Changes will appear here</span>
                  </div>
                </div>
              ) : (
                <div className="tw-flex tw-grow tw-flex-col tw-gap-4 tw-rounded-2xl tw-bg-[#F9F8F5] tw-p-4">
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    {!!colliding && (
                      <span className="tw-text-sm tw-leading-5 tw-text-[#686868]">That day already has a lesson:</span>
                    )}
                    <div className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-border-2 tw-border-b-4 tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-1">
                      <button
                        type="button"
                        disabled={!colliding}
                        title={colliding ? undefined : 'That day is open — only this lesson moves.'}
                        className={strategyPillClass(effective === 'push', !colliding)}
                        onClick={() => setStrategy('push')}
                      >
                        Move lessons
                      </button>
                      <button
                        type="button"
                        className={strategyPillClass(effective === 'replace')}
                        onClick={() => setStrategy('replace')}
                      >
                        {colliding ? 'Replace lesson' : 'Move only this lesson'}
                      </button>
                    </div>
                  </div>

                  {moveRow(moverTitle, source, target)}

                  {replaced ? (
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-md tw-bg-white tw-px-3 tw-py-1">
                        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-leading-[18px] tw-text-neutral-950">
                          {lessonName(collidingTitle)}
                        </span>
                        <span className="tw-shrink-0 tw-rounded tw-border tw-border-b-2 tw-border-solid tw-border-[#FFD8CE] tw-bg-[#FDEDEA] tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-bold tw-leading-4 tw-text-[#693314]">
                          Replaced
                        </span>
                      </div>
                      <span className="tw-text-sm tw-leading-5 tw-text-[#686868]">
                        {lessonName(collidingTitle)} gets removed from the schedule
                      </span>
                    </div>
                  ) : (
                    moves.length > 0 && (
                      <>
                        <span className="tw-text-xs tw-font-bold tw-leading-4 tw-text-neutral-950">
                          {moves.length === 1
                            ? '1 subsequent lesson shifts forward'
                            : `${moves.length} subsequent lessons shift forward`}
                        </span>
                        <div className="tw-flex tw-max-h-32 tw-flex-col tw-gap-0.5 tw-overflow-y-auto">
                          {moves.map(move => moveRow(schedule[move.index]?.title ?? '', move.from, move.to, true))}
                        </div>
                      </>
                    )
                  )}

                  {responseCount > 0 && (
                    <span className="tw-text-xs tw-leading-4 tw-text-[#686868]">
                      {responseCount === 1
                        ? '1 student response from today is kept.'
                        : `${responseCount} student responses from today are kept.`}{' '}
                      Students take this lesson again on {shortDay(target)}.
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="tw-flex tw-h-11 tw-shrink-0 tw-items-center tw-justify-end tw-gap-3">
            <Button
              label="Cancel"
              outlined
              disabled={saving}
              onClick={onClose}
              className="!tw-h-11 !tw-rounded-full !tw-border-2 !tw-px-4 !tw-font-atkinson !tw-font-bold !tw-text-base !tw-text-indigo-600 hover:!tw-text-white"
            />
            <Button
              label={
                !target
                  ? 'Pick a new date'
                  : effective === 'push' && moves.length > 0
                  ? 'Move All'
                  : `Move to ${shortDay(target)}`
              }
              disabled={!target}
              loading={saving}
              onClick={() => target && onConfirm(target, moves, replaced)}
              className="tw-min-w-40 tw-whitespace-nowrap !tw-h-11 !tw-rounded-full !tw-border-none !tw-bg-indigo-600 !tw-px-4 !tw-font-atkinson !tw-text-base !tw-font-bold disabled:!tw-bg-neutral-200 disabled:!tw-text-neutral-600 disabled:!tw-opacity-100"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ExitSlipSnapshotRescheduleModal;
